import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Country,
  CountryCreate,
  CountryUpdate,
  TripCreate,
  TripUpdate,
  TripWithCountries,
} from '@travel-planner/shared';
import { api } from '../api/client';

export const TRIPS_KEY = ['trips'] as const;

export function useTrips() {
  return useQuery<TripWithCountries[]>({
    queryKey: TRIPS_KEY,
    queryFn: () => api.get<TripWithCountries[]>('/trips'),
  });
}

export function useCreateTrip() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TripCreate) => api.post<TripWithCountries>('/trips', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: TRIPS_KEY }),
  });
}

export function useUpdateTrip() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: TripUpdate }) =>
      api.patch<TripWithCountries>(`/trips/${id}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: TRIPS_KEY }),
  });
}

export function useDeleteTrip() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<void>(`/trips/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: TRIPS_KEY }),
  });
}

export function useCreateCountry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tripId, input }: { tripId: string; input: CountryCreate }) =>
      api.post<Country>(`/trips/${tripId}/countries`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: TRIPS_KEY }),
  });
}

export function useUpdateCountry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      tripId,
      countryId,
      patch,
    }: {
      tripId: string;
      countryId: string;
      patch: CountryUpdate;
    }) => api.patch<Country>(`/trips/${tripId}/countries/${countryId}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: TRIPS_KEY }),
  });
}

export function useDeleteCountry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tripId, countryId }: { tripId: string; countryId: string }) =>
      api.del<void>(`/trips/${tripId}/countries/${countryId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: TRIPS_KEY }),
  });
}
